import React, { Suspense, lazy, useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import { MobileTopBar } from "@/components/MobileTopBar";
import SEO from "@/components/SEO";
import SubscriptionTimeline from "@/components/profile/SubscriptionTimeline";
import MyGiftCard from "@/components/MyGiftCard";  // iter1118b — „Моят подарък"
import PatientRoleBanner from "@/components/profile/PatientRoleBanner";
import AutoCycleSettings from "@/components/profile/AutoCycleSettings";
import CyclePhaseCard from "@/components/profile/CyclePhaseCard";
import PerinatalAutoSuggestCard from "@/components/profile/PerinatalAutoSuggestCard";  // iter1108
import PerinatalProfilePane from "@/components/profile/PerinatalProfilePane";
import MigrateMyLabsCard from "@/components/lab_migration/MigrateMyLabsCard";  // iter754
import LabCoverageHeatMap from "@/components/lab_migration/LabCoverageHeatMap";  // iter755
import HealthConditionsCard from "@/components/health_conditions/HealthConditionsCard";  // iter759
import MedsManagerCard from "@/components/profile/MedsManagerCard";
import ProfileMyDataHub from "@/components/profile/ProfileMyDataHub";  // iter1008 — „Моите данни"
import KhiKdigoRiskCard from "@/components/khi/KhiKdigoRiskCard";  // iter1008 — албумин + карта на риска
import HealthSummaryCard from "@/components/profile/HealthSummaryCard";  // iter894 — aggregated health record numbers
import TwinReadinessCard from "@/components/profile/TwinReadinessCard";  // iter1068 — какво задвижва близнаците
import BhiPreviewCard from "@/components/BhiPreviewCard";  // iter902 — BHI snapshot preview
import DoctorShareSection from "@/components/profile/DoctorShareSection";  // iter567 — Doctor invite + approval flow
import GdprSelfService from "@/components/GdprSelfService";
import FhirExportPanel from "@/components/FhirExportPanel";  // iter1002 — FHIR R4 споделяне
import PrivacyDashboard from "@/components/privacy/PrivacyDashboard";
import HiddenCardsPanel from "@/components/HiddenCardsPanel";
import ProfileGuide from "@/components/guides/ProfileGuide";
import {
  Loader2, CheckCircle2, CircleDashed, Sparkles, CreditCard,
  User, Activity, Ruler, Heart, Target, FlaskConical, Moon, Utensils, ArrowRight,
  ShieldCheck, ClipboardList, Database,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const ProfileEditModal = lazy(() => import("@/components/ProfileEditModal"));
const ExportPanel = lazy(() => import("@/components/ExportPanel"));
const TrialDashboard = lazy(() => import("@/components/TrialDashboard"));
const SecuritySettings = lazy(() => import("@/components/SecuritySettings"));
const FeedbackPanel = lazy(() => import("@/components/FeedbackPanel"));
const WeeklyReport = lazy(() => import("@/components/WeeklyReport"));
const DailyDiaryNote = lazy(() => import("@/components/DailyDiaryNote"));
const CreditPurchase = lazy(() => import("@/components/CreditPurchase"));
const AutoDiagnosisPanel = lazy(() => import("@/components/breast_intel/AutoDiagnosisPanel"));


/** Compute category-level completeness with item breakdown. */
function buildCategories(user) {
  const p = user?.profile || {};
  const pe = p.personal || {};
  const bd = p.body || {};
  const ms = p.measurements || {};
  const ls = p.lifestyle || {};
  const hl = p.health || {};
  // iter1020 — cross-source flags (BHI, health_conditions, family_history)
  const flags = user?.health_flags || {};
  // Treat the strings "None"/"null"/"N/A"/"-" as empty so free-text stubs
  // from old onboarding flows don't falsely mark a section as complete.
  const nonEmpty = (v) => {
    const s = (typeof v === "string" ? v : "").trim().toLowerCase();
    return s !== "" && !["none", "null", "n/a", "-"].includes(s);
  };

  return [
    {
      key: "personal", icon: User,
      label_bg: "Основни данни", label_en: "Personal",
      items: [
        { ok: !!pe.fullName, label_bg: "Пълно име", label_en: "Full name" },
        { ok: !!pe.age,      label_bg: "Възраст",    label_en: "Age" },
        { ok: !!pe.gender,   label_bg: "Пол",        label_en: "Gender" },
      ],
    },
    {
      key: "body", icon: Activity,
      label_bg: "Телесни данни", label_en: "Body metrics",
      items: [
        { ok: !!bd.height, label_bg: "Височина", label_en: "Height" },
        { ok: !!bd.weight, label_bg: "Тегло",     label_en: "Weight" },
      ],
    },
    {
      key: "measurements", icon: Ruler,
      label_bg: "Обиколки", label_en: "Measurements",
      items: [
        { ok: !!ms.neck,  label_bg: "Шия",  label_en: "Neck" },
        { ok: !!ms.waist, label_bg: "Талия", label_en: "Waist" },
        { ok: !!ms.hip,   label_bg: "Ханш",  label_en: "Hip" },
      ],
    },
    {
      key: "lifestyle", icon: Moon,
      label_bg: "Начин на живот", label_en: "Lifestyle",
      items: [
        { ok: !!ls.activityLevel, label_bg: "Ниво на активност", label_en: "Activity level" },
        { ok: !!ls.sleepHours,    label_bg: "Часове сън",         label_en: "Sleep hours" },
        { ok: !!ls.stressLevel,   label_bg: "Ниво на стрес",      label_en: "Stress level" },
      ],
    },
    {
      key: "diet", icon: Utensils,
      label_bg: "Хранителни навици", label_en: "Diet",
      items: [
        { ok: !!ls.dietType,    label_bg: "Тип диета", label_en: "Diet type" },
        { ok: !!ls.primaryGoal, label_bg: "Основна цел", label_en: "Primary goal" },
      ],
    },
    {
      key: "health", icon: Heart,
      label_bg: "Здравни данни", label_en: "Health data",
      items: [
        { ok: nonEmpty(hl.currentConditions) || !!flags.has_conditions,     label_bg: "Текущи състояния",    label_en: "Current conditions" },
        { ok: nonEmpty(hl.medications)       || !!flags.has_medications,    label_bg: "Лекарства",            label_en: "Medications" },
        { ok: nonEmpty(hl.familyHistory)     || !!flags.has_family_history, label_bg: "Фамилна анамнеза",     label_en: "Family history" },
      ],
    },
    {
      key: "goals", icon: Target,
      label_bg: "Цели", label_en: "Goals",
      items: [
        { ok: !!(p.goals?.length || ls.primaryGoal), label_bg: "Здравни цели", label_en: "Health goals" },
      ],
    },
    {
      key: "labs", icon: FlaskConical,
      label_bg: "Лабораторни данни", label_en: "Lab tests",
      items: [
        // iter689 — check multiple sources, not just profile.has_labs
        { ok: !!(p.has_labs || p.labValues || (user.lab_results || []).length > 0 || (user.lab_uploads || []).length > 0),
          label_bg: "Качени лабораторни резултати", label_en: "Uploaded lab results" },
      ],
    },
  ];
}


export default function ProfilePageFull() {
  const { user, refreshUser } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [editOpen, setEditOpen] = useState(false);
  const [showAnalysesPacks, setShowAnalysesPacks] = useState(false);  // iter419 — expand to show buy analyses
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return "profile";
    try { return localStorage.getItem("profile-active-tab") || "profile"; } catch { return "profile"; }
  });
  useEffect(() => {
    try { localStorage.setItem("profile-active-tab", activeTab); } catch (_e) { /* ignore quota errors */ }
  }, [activeTab]);
  const navigate = useNavigate();
  const location = useLocation();
  // iter1008 — „Моите данни": deep-link ?tab=mydata + отваряне на формата за профила
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("tab");
    if (t && ["profile", "mydata", "subscription", "activity", "security"].includes(t)) {
      setActiveTab(t);
    }
    const openEdit = () => setEditOpen(true);
    window.addEventListener("profile:edit:open", openEdit);
    return () => window.removeEventListener("profile:edit:open", openEdit);
  }, [location.search]);

  // iter419 — auto-open analyses packs when arriving via ?buy=analyses
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("buy") === "analyses") {
      setShowAnalysesPacks(true);
      setActiveTab("subscription");
      setTimeout(() => {
        const el = document.querySelector('[data-testid="profile-analyses-packs"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [location.search]);

  // iter933 — Removed forced redirect that hid the personal profile from
  // doctors/admins. They now see the same personal profile as patients
  // (they can be patients too!) with a prominent link to switch to the
  // clinician workspace.  See ProfileRoleSwitcher below.
  //
  // Legacy iter314 behaviour (auto-redirect to /doctor/profile) is
  // preserved only when the user has NEVER opened their personal
  // profile before — driven by a localStorage flag so returning users
  // can still choose freely.
  //   useEffect(() => {
  //     if (user && (user.role === "clinician" || user.role === "admin")) {
  //       navigate("/doctor/profile", { replace: true });
  //     }
  //   }, [user, navigate]);

  const categories = buildCategories(user);
  const totalItems = categories.reduce((sum, c) => sum + c.items.length, 0);
  const doneItems  = categories.reduce((sum, c) => sum + c.items.filter(i => i.ok).length, 0);
  const pct = totalItems ? Math.round((doneItems / totalItems) * 100) : 0;

  const cardCls = `rounded-xl p-4 sm:p-5 border
    ${isDark ? "bg-[hsl(222,47%,10%)] border-white/5" : "bg-white border-gray-200"}`;

  // Plan + credits info (credits visible ТОЛКОВА тук — скрити from inline actions)
  const sub = user?.subscription || {};
  const isAdmin = user?.role === "admin";
  const tierLabel = isAdmin ? "Admin" : {
    active:   "Pro",
    trialing: bg ? "Пробен" : "Trial",
  }[sub.status] || (bg ? "Безплатен" : "Free");

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-gray-900"}`}
      data-testid="profile-page-full">
      <SEO
        title={bg ? "Профил — Nutramilo" : "Profile — Nutramilo"}
        description={bg ? "Лични данни, здравен профил, лаборатории, цели, абонамент и сигурност."
          : "Personal data, health record, labs, goals, subscription and security."}
        path="/dashboard/profile"
      />
      <DashboardHeader />
      <MobileTopBar title={bg ? "Профил" : "Profile"} fallbackTo="/dashboard" testid="profile-mobile-topbar" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-6">

        {/* iter933 — Doctors + admins get a persistent switcher to their
            clinician workspace. Without this they used to be silently
            redirected to /doctor/profile and lost access to their
            personal patient data (they can be patients too). */}
        {(user?.role === "clinician" || user?.role === "admin") && (
          <div
            className={`mb-4 p-3 rounded-xl border flex items-center justify-between gap-3 flex-wrap ${
              isDark ? "border-cyan-400/25 bg-cyan-500/10 text-cyan-100"
                     : "border-cyan-200 bg-cyan-50 text-cyan-900"
            }`}
            data-testid="profile-role-switcher"
          >
            <div className="text-xs">
              {bg
                ? "👀 Виждате Вашия ЛИЧЕН профил (като пациент). Можете да превключите към клиничния workspace, за да управлявате пациенти."
                : "👀 You are viewing your PERSONAL profile (as a patient). Switch to the clinician workspace to manage patients."}
            </div>
            <button
              type="button"
              onClick={() => navigate("/doctor/profile")}
              className={`text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap ${
                isDark ? "bg-cyan-500/30 hover:bg-cyan-500/40 text-cyan-100"
                       : "bg-cyan-600 hover:bg-cyan-700 text-white"
              }`}
              data-testid="switch-to-doctor-profile-btn"
            >
              🩺 {bg ? "Отиди в докторски профил" : "Open doctor workspace"} →
            </button>
          </div>
        )}

        <header className="mb-5">
          <h1 className="text-xl sm:text-2xl font-bold">
            {bg ? "Профил и настройки" : "Profile & Settings"}
          </h1>
          <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {bg ? "Лични данни, здравен профил, абонамент и сигурност."
              : "Personal data, health record, subscription and security."}
          </p>
        </header>

        {/* Patient role notification — Iteration 257 (compliance) */}
        <div className="mb-5">
          <PatientRoleBanner user={user} />
        </div>

        {/* Profile completeness HERO */}
        <section className={`${cardCls} mb-5`} data-testid="profile-completeness">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className={`text-[11px] uppercase tracking-wider font-semibold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                {bg ? "Попълненост на профила" : "Profile completeness"}
              </div>
              <div className="text-2xl sm:text-3xl font-bold mt-0.5" data-testid="completeness-pct">{pct}%</div>
              <div className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {doneItems} / {totalItems} {bg ? "полета попълнени" : "fields filled"} · {categories.filter(c => c.items.every(i => i.ok)).length} / {categories.length} {bg ? "секции завършени" : "sections complete"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* iter226e+2 — постоянно видим бутон за редакция, дори при 100% */}
              <button
                onClick={() => setEditOpen(true)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors
                  ${isDark ? "bg-white/10 text-white hover:bg-white/15"
                           : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                data-testid="edit-profile-btn"
                title={bg ? "Отвори формата за редакция на профила" : "Open profile edit form"}
              >
                <User className="w-3.5 h-3.5" />
                {bg ? "Редактирай" : "Edit"}
              </button>
              {/* Feb 2026 — Rerun onboarding: patients who want to change their
                  primary goal (weight_loss → maintenance) or re-drive the full
                  wizard can do so without wiping completedOnboarding flag. */}
              <button
                onClick={() => navigate("/onboarding?force=1")}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors
                  ${isDark ? "bg-violet-500/15 text-violet-200 hover:bg-violet-500/25"
                           : "bg-violet-100 text-violet-900 hover:bg-violet-200"}`}
                data-testid="rerun-onboarding-btn"
                title={bg ? "Мини през целия wizard отново (без изтриване на профилни данни)" : "Re-run the full wizard (profile data preserved)"}
              >
                <Sparkles className="w-3.5 h-3.5" />
                {bg ? "Пусни отново" : "Rerun"}
              </button>
              {pct < 100 && (
                <button
                  onClick={() => setEditOpen(true)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors
                    ${isDark ? "bg-emerald-500/30 text-emerald-200 hover:bg-emerald-500/40"
                             : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
                  data-testid="complete-profile-cta"
                >
                  <Sparkles className="w-4 h-4" />
                  {bg ? "Довърши всичко" : "Complete all"}
                </button>
              )}
            </div>
          </div>
          <div className="mt-3 h-2 rounded-full overflow-hidden bg-gray-200 dark:bg-white/10">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 transition-all"
              style={{ width: `${pct}%` }}
              data-testid="completeness-bar"
            />
          </div>

          {/* Category breakdown */}
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="completeness-categories">
            {categories.map((c) => {
              const Icon = c.icon;
              const cDone = c.items.filter(i => i.ok).length;
              const cTotal = c.items.length;
              const cPct = Math.round((cDone / cTotal) * 100);
              const complete = cPct === 100;
              return (
                <div
                  key={c.key}
                  className={`rounded-xl p-3 border transition-colors
                    ${complete
                      ? (isDark ? "bg-emerald-500/5 border-emerald-900/40" : "bg-emerald-50/50 border-emerald-200")
                      : (isDark ? "bg-white/[0.02] border-white/10" : "bg-gray-50 border-gray-200")}`}
                  data-testid={`profile-category-${c.key}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0
                        ${complete
                          ? (isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700")
                          : (isDark ? "bg-white/10 text-gray-400" : "bg-white text-gray-500")}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <h3 className="text-sm font-semibold truncate">{bg ? c.label_bg : c.label_en}</h3>
                    </div>
                    <span className={`text-xs font-semibold flex-shrink-0 ${complete ? "text-emerald-500" : (isDark ? "text-gray-500" : "text-gray-500")}`}>
                      {cDone}/{cTotal}
                    </span>
                  </div>
                  <ul className="space-y-0.5 mb-2">
                    {c.items.map((it, i) => (
                      <li key={i} className="flex items-center gap-1.5 text-[11px]">
                        {it.ok
                          ? <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                          : <CircleDashed className={`w-3 h-3 shrink-0 ${isDark ? "text-gray-500" : "text-gray-400"}`} />}
                        <span className={it.ok ? (isDark ? "text-gray-400" : "text-gray-600") : "opacity-70"}>
                          {bg ? it.label_bg : it.label_en}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {!complete && (
                    <button
                      onClick={() => {
                        // iter686 — labs category requires file upload, not
                        // profile form. Route them to the Lab Twin page.
                        if (c.key === "labs") {
                          navigate("/dashboard/lab-twin");
                        } else {
                          setEditOpen(true);
                        }
                      }}
                      className={`text-[11px] font-semibold inline-flex items-center gap-0.5 transition-colors
                        ${isDark ? "text-sky-300 hover:text-sky-200" : "text-sky-700 hover:text-sky-800"}`}
                      data-testid={`profile-category-cta-${c.key}`}
                    >
                      {bg ? "Попълни" : "Fill in"}
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Subscription + credits (ЕДИНСТВЕНО място за credits visibility) */}
        <section className={`${cardCls} mb-5 flex items-center justify-between gap-3 flex-wrap`} data-testid="profile-subscription">
          <div>
            <div className={`text-[11px] uppercase tracking-wider font-semibold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
              {bg ? "Абонамент" : "Subscription"}
            </div>
            <div className="text-base sm:text-lg font-bold mt-0.5">
              {tierLabel}
              {!isAdmin && (
                <span className={`ml-2 text-xs font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  · {user?.credits ?? 0} {bg ? "AI анализа" : "AI analyses"}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                // iter531 — Auto-switch to Subscription tab so the panel
                // becomes visible. Преди: бутонът само toggle-ваше state-а
                // без да сменя таба, и потребителят виждаше нищо.
                setActiveTab("subscription");
                setShowAnalysesPacks((v) => !v);
              }}
              data-testid="profile-toggle-analyses-packs"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${isDark ? "bg-amber-500/20 text-amber-200 hover:bg-amber-500/30"
                         : "bg-amber-500 text-white hover:bg-amber-600"}`}
            >
              <Sparkles className="w-4 h-4" />
              {bg ? (showAnalysesPacks ? "Затвори" : "Купи анализи") : (showAnalysesPacks ? "Close" : "Buy analyses")}
            </button>
            <Link
              to="/upgrade"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${isDark ? "bg-violet-500/30 text-violet-200 hover:bg-violet-500/40"
                         : "bg-violet-600 text-white hover:bg-violet-700"}`}
              data-testid="profile-manage-subscription"
            >
              <CreditCard className="w-4 h-4" />
              {bg ? "Управление" : "Manage"}
            </Link>
            {/* iter1118 — директна връзка към ръководството за цените */}
            <Link
              to="/credits-guide"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${isDark ? "bg-amber-500/20 text-amber-200 hover:bg-amber-500/30"
                         : "bg-amber-100 text-amber-800 hover:bg-amber-200"}`}
              data-testid="profile-credits-guide-link"
            >
              {bg ? "Какво струват действията" : "What actions cost"}
            </Link>
          </div>
        </section>

        {/* iter419 — Analysis packs expandable section (visible само в Subscription tab) */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="profile-tabs">
          {/* iter1090 — sticky tabs on mobile: остават под MobileTopBar */}
          <TabsList className={`flex w-full h-auto p-1 rounded-2xl gap-1 mb-5 overflow-x-auto sticky top-[52px] md:static z-30 backdrop-blur-xl ${
            isDark ? "bg-slate-900/80 border border-slate-800" : "bg-slate-100/90 border border-slate-200"
          }`}>
            <TabsTrigger value="profile" data-testid="profile-tab-profile"
              className="flex-1 min-w-[120px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-bold">
              <User className="w-3.5 h-3.5" />
              {bg ? "Здравен профил" : "Health profile"}
            </TabsTrigger>
            <TabsTrigger value="mydata" data-testid="profile-tab-mydata"
              className="flex-1 min-w-[120px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-bold">
              <Database className="w-3.5 h-3.5" />
              {bg ? "Моите данни" : "My data"}
            </TabsTrigger>
            <TabsTrigger value="subscription" data-testid="profile-tab-subscription"
              className="flex-1 min-w-[120px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-bold">
              <CreditCard className="w-3.5 h-3.5" />
              {bg ? "Абонамент" : "Subscription"}
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="profile-tab-activity"
              className="flex-1 min-w-[120px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-bold">
              <ClipboardList className="w-3.5 h-3.5" />
              {bg ? "Активност" : "Activity"}
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="profile-tab-security"
              className="flex-1 min-w-[120px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-bold">
              <ShieldCheck className="w-3.5 h-3.5" />
              {bg ? "Сигурност" : "Security"}
            </TabsTrigger>
          </TabsList>

          {/* === Tab 1: Здравен профил === */}
          <TabsContent value="profile" className="space-y-5 mt-0" data-testid="profile-tab-content-profile">
            {/* iter354 — Auto-cycle tracker (only for female users) */}
            {(user?.profile?.personal?.gender === "female" || user?.profile?.personal?.gender === "жена") && (
              <div data-testid="auto-cycle-section">
                <AutoCycleSettings />
                {/* iter1092 — Изнесен, ясен UI за текуща фаза + explainer.
                    Отключва Hormonal twin от Twin Network. */}
                <div className="mt-4">
                  <CyclePhaseCard />
                </div>
              </div>
            )}
            {/* iter532 — Perinatal profile (pregnancy / lactation / postpartum + chronic conditions) */}
            {(user?.profile?.personal?.gender === "female" ||
              user?.profile?.personal?.gender === "жена" ||
              user?.role === "admin") && (
              <>
                {/* iter1108 — Auto-suggest perinatal stage from LMP + delivery date */}
                <PerinatalAutoSuggestCard />
                <PerinatalProfilePane />
              </>
            )}
            <div className={`${cardCls}`}>
              <h2 className={`text-sm font-bold mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>
                {bg ? "Преглед на профила" : "Profile overview"}
              </h2>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                {bg
                  ? "Бутонът „Редактирай“ по-горе отваря пълната форма за лични данни, обиколки, цели, навици, заболявания, лекарства и фамилна анамнеза."
                  : "Use the „Edit“ button above to open the full form for personal data, measurements, goals, lifestyle, conditions, medications and family history."}
              </p>
            </div>

            {/* iter894 — Здравно резюме от досието (anthropometry + BMR/TDEE +
                персонализиран DIL + VAIS twin) — read-only, редакция чрез
                бутона „Редактирай профил" отгоре. */}
            <HealthSummaryCard />

            {/* iter1068 — „Какво задвижва близнаците": състояние на всеки
                близнак, точно какво липсва и пряк път до въвеждането. */}
            <TwinReadinessCard />

            {/* iter902 — BHI live preview: score, active zones, conditions */}
            <BhiPreviewCard testid="profile-bhi-preview-card" />

            {/* iter1008 — данните се попълват в таба „Моите данни" */}
            <button onClick={() => setActiveTab("mydata")}
              className={`${cardCls} w-full text-left hover:shadow-md transition-shadow`}
              data-testid="profile-goto-mydata">
              <div className="flex items-center gap-3">
                <Database className="w-5 h-5 text-cyan-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">
                    {bg ? "Моите данни — лаборатории, автодиагноза, заболявания, лекарства"
                        : "My data — labs, auto-diagnosis, conditions, medications"}
                  </div>
                  <div className={`text-xs mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    {bg ? "Качването на изследвания, автодиагнозата, албуминът в урината, заболяванията и лекарствата вече са в отделния таб „Моите данни“."
                        : "Uploads, auto-diagnosis, urine albumin, conditions and medications now live in the „My data“ tab."}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-cyan-500 shrink-0" />
              </div>
            </button>
          </TabsContent>

          {/* === Tab 2: Моите данни === */}
          <TabsContent value="mydata" className="space-y-5 mt-0" data-testid="profile-tab-content-mydata">
            <ProfileMyDataHub />

            {/* Автодиагноза от натрупаните лабораторни данни */}
            <Suspense fallback={<div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}>
              <AutoDiagnosisPanel lang="bg" />
            </Suspense>

            {/* Албумин в урината + карта на риска по KDIGO */}
            <KhiKdigoRiskCard
              cardCls={isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}
              isDark={isDark} />

            {/* iter754 — Migrate My Labs — 1-click унификация на всичките lab колекции */}
            <MigrateMyLabsCard />

            {/* iter755 — Lab Coverage Heat-Map — визуална карта на 44+ маркера */}
            <LabCoverageHeatMap />

            {/* iter759 — Structured Health Conditions */}
            <HealthConditionsCard />

            {/* Общ списък „лекарства, добавки и билки" — захранва всички модули */}
            <MedsManagerCard />
          </TabsContent>

          {/* === Tab 2: Абонамент === */}
          <TabsContent value="subscription" className="space-y-5 mt-0" data-testid="profile-tab-content-subscription">
            {/* iter1118b — активният „Подарък на пациент" (ако има) */}
            <MyGiftCard isDark={isDark} />
            {showAnalysesPacks && (
              <section data-testid="profile-analyses-packs">
                <Suspense fallback={<div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}>
                  <CreditPurchase currentCredits={user?.credits ?? 0} />
                </Suspense>
              </section>
            )}
            <SubscriptionTimeline />
            <Suspense fallback={<div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}>
              <TrialDashboard />
            </Suspense>
          </TabsContent>

          {/* === Tab 3: Активност === */}
          <TabsContent value="activity" className="space-y-5 mt-0" data-testid="profile-tab-content-activity">
            <Suspense fallback={<div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
                <div data-testid="profile-weekly-report"><WeeklyReport /></div>
                <div data-testid="profile-daily-note"><DailyDiaryNote /></div>
              </div>
              {/* iter567 — Doctor invite + approval flow */}
              <DoctorShareSection />
              {/* iter327Z — "Възстанови скритите карти" (показва се при ≥3 скрити модула) */}
              <HiddenCardsPanel />
            </Suspense>
          </TabsContent>

          {/* === Tab 4: Сигурност и поверителност === */}
          <TabsContent value="security" className="space-y-5 mt-0" data-testid="profile-tab-content-security">
            <Suspense fallback={<div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
                <div className="space-y-4">
                  <SecuritySettings />
                  <div className={`p-4 rounded-2xl ${isDark ? "bg-[hsl(223,47%,11%)] border border-white/5" : "bg-white border border-gray-100 shadow-sm"}`}>
                    <GdprSelfService />
                  </div>
                </div>
                <div className="space-y-4">
                  <ExportPanel />
                  <FhirExportPanel />
                  <FeedbackPanel />
                  {/* iter367 Phase-3 — Privacy Dashboard (GAP 4/5/7/8) */}
                  <div className={`p-4 rounded-2xl ${isDark ? "bg-[hsl(223,47%,11%)] border border-white/5" : "bg-white border border-gray-100 shadow-sm"}`}>
                    <PrivacyDashboard isDark={isDark} />
                  </div>
                </div>
              </div>
            </Suspense>
          </TabsContent>
        </Tabs>
      </main>

      <Suspense fallback={null}>
        {editOpen && (
          <ProfileEditModal
            open={editOpen}
            onClose={() => setEditOpen(false)}
            onSaved={() => { setEditOpen(false); refreshUser?.(); }}
          />
        )}
      </Suspense>
      <ProfileGuide />
    </div>
  );
}
